import React from 'react';
import Card from './Card';
import TextLink from './TextLink';
import { cn } from './cn';

/** Financial list row with aligned amount, progress, and editable detail. */
export default function ProgressRow({
  icon, label, value, valueDetail, detail, progress, actions, emptyDetail = 'No budget',
  onClick, onDetailClick, detailLabel, expanded, controls, highlighted, className, children, ...props
}) {
  const hasBudget = progress != null;
  const used = Math.min(100, Math.max(0, progress || 0));
  const overBudget = progress > 100;
  const ringTone = overBudget ? 'text-bun-danger' : progress >= 90 ? 'text-bun-warning' : 'text-bun-success';
  return (
    <Card.Row layout="block" highlighted={highlighted} className={cn('group relative border-b-0 py-[18px] after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-bun-line-soft last:after:hidden', className)} {...props}>
      <div className={cn('grid items-center gap-x-2 sm:gap-x-4', actions ? 'grid-cols-[48px_minmax(0,1fr)_auto_28px_40px]' : 'grid-cols-[48px_minmax(0,1fr)_auto_28px]')}>
        <div className="flex h-12 w-12 items-center justify-center">{icon}</div>
        <TextLink as="button" muted disabled={!onClick} onClick={onClick} aria-label={label} aria-expanded={expanded} aria-controls={controls} className={cn('min-w-0 justify-start text-left', expanded !== undefined && 'after:absolute after:inset-0 after:content-[""]')}>
          <span className="block min-w-0 w-full">
            <span className="block truncate text-bun-body leading-6 font-strong text-bun-text">{label}</span>
            <span className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-bun-heading font-semibold tabular-nums text-bun-text">{value}</span>
              {valueDetail && <span className="text-bun-meta leading-5 font-book text-bun-stone">{valueDetail}</span>}
            </span>
          </span>
        </TextLink>
        <TextLink
          as="button" muted disabled={!onDetailClick} onClick={onDetailClick} aria-label={detailLabel}
          className={cn('justify-end text-right', expanded !== undefined && (onDetailClick ? 'relative z-10' : 'pointer-events-none'))}
        >
          <span className="block">
            <span className="block text-bun-meta leading-5 font-book text-bun-stone">{hasBudget ? 'Budget' : emptyDetail}</span>
            {hasBudget && detail && <span className={cn('block whitespace-nowrap text-bun-heading font-semibold tabular-nums', overBudget ? 'text-bun-danger' : 'text-bun-success')}>{detail}</span>}
          </span>
        </TextLink>
        <svg viewBox="0 0 40 40" className={cn('h-7 w-7 -rotate-90', hasBudget && ringTone)}
          role={hasBudget ? 'progressbar' : undefined} aria-hidden={!hasBudget || undefined}
          aria-label={hasBudget ? `${label} budget used` : undefined}
          aria-valuemin={hasBudget ? 0 : undefined} aria-valuemax={hasBudget ? 100 : undefined} aria-valuenow={hasBudget ? used : undefined}>
          <circle cx="20" cy="20" r="17" fill="none" stroke="currentColor" strokeWidth="4" className="text-bun-line-soft" />
          {hasBudget && used > 0 && <circle cx="20" cy="20" r="17" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" pathLength="100" strokeDasharray={`${used} 100`} />}
        </svg>
        {actions && <div className={cn('relative z-10 flex justify-end', expanded === undefined && 'opacity-100 [@media(hover:hover)]:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100')}>{actions}</div>}
      </div>
      {children && <div className={cn('mt-3', expanded !== undefined && 'relative z-10')}>{children}</div>}
    </Card.Row>
  );
}
